import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { RefPrice } from "../lib/price";

const pin = (selected, n) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:26px;height:26px"><div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${selected ? "#2B3029" : "#8C6C50"};border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>${n > 1 ? `<span style="position:absolute;top:-8px;right:-10px;background:#fff;color:#2B3029;border-radius:9px;font:600 11px/16px sans-serif;padding:0 5px;box-shadow:0 1px 3px rgba(0,0,0,.3)">${n}</span>` : ""}</div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
    popupAnchor: [0, -24],
  });

function FitBounds({ points }) {
  const map = useMap();
  const sig = JSON.stringify(points);
  useEffect(() => {
    const pts = JSON.parse(sig);
    if (pts.length === 1) map.setView(pts[0], 15);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40], maxZoom: 15 });
  }, [map, sig]);
  return null;
}

const PopupItem = ({ l, selected, toggle, compact }) => (
  <div className={compact ? "py-2 border-b border-[#E5E1D8] last:border-0" : ""} data-testid={`map-popup-${l.id}`}>
    {!compact && l.image_urls?.[0] && <img src={l.image_urls[0]} alt={l.title} className="w-full h-28 object-cover rounded mb-2" />}
    {l.brand && <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#8C6C50]">{l.brand}</div>}
    <div className="font-serif text-base text-[#1A1A1A] leading-tight">{l.title}</div>
    <div className="text-xs text-[#666] mt-0.5">{[l.residence_name, [l.postal_code, l.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}</div>
    <div className="mt-1"><RefPrice l={l} /></div>
    <div className="mt-2 flex items-center justify-between gap-2">
      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
        <input type="checkbox" checked={!!selected[l.id]} onChange={() => toggle(l)} className="accent-[#2B3029]" data-testid={`map-select-${l.id}`} />
        Sélectionner
      </label>
      <Link to={`/listings/${l.id}`} className="text-xs font-medium text-[#8C6C50] underline" data-testid={`map-open-${l.id}`}>Voir l'annonce</Link>
    </div>
  </div>
);

export const CatalogMap = ({ listings, selected, toggle }) => {
  const located = listings.filter((l) => typeof l.lat === "number" && typeof l.lon === "number");
  const groups = Object.values(located.reduce((g, l) => {
    const k = `${l.lat.toFixed(5)},${l.lon.toFixed(5)}`;
    (g[k] = g[k] || []).push(l);
    return g;
  }, {}));
  const points = groups.map((g) => [g[0].lat, g[0].lon]);
  const missing = listings.length - located.length;
  return (
    <div data-testid="catalog-map">
      <div className="h-[65vh] min-h-[420px] rounded-md overflow-hidden border border-[#E5E1D8] relative z-0">
        <MapContainer center={[48.8566, 2.3522]} zoom={11} scrollWheelZoom className="h-full w-full">
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={points} />
          {groups.map((g) => (
            <Marker key={g[0].id} position={[g[0].lat, g[0].lon]} icon={pin(g.some((l) => selected[l.id]), g.length)}>
              <Popup>
                <div className="w-56 max-h-72 overflow-y-auto">
                  {g.length > 1 && <div className="text-xs font-semibold text-[#2B3029] mb-1">{g.length} annonces à cette adresse</div>}
                  {g.map((l) => <PopupItem key={l.id} l={l} selected={selected} toggle={toggle} compact={g.length > 1} />)}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      {missing > 0 && (
        <p className="text-xs text-[#666] mt-3" data-testid="catalog-map-missing">
          {missing} annonce{missing > 1 ? "s" : ""} sans adresse localisable {missing > 1 ? "ne sont" : "n'est"} pas sur la carte (visible{missing > 1 ? "s" : ""} en vue liste).
        </p>
      )}
    </div>
  );
};
